import { fullUrl } from 'scripts/fullUrl'

// OEM / ホワイトラベル対応: locked-api の /api/v1/public/brand レスポンス型。
export interface Brand {
  host: string | null
  productDisplayName: string
  // OEM: コピーライト表示用の partner 会社名（console の Partner#name 由来）。LOCKED は null。
  companyName: string | null
  logoUrl: string
  // OEM: partner が認証画面に表示する favicon の URL。未設定 / LOCKED は null。
  faviconUrl: string | null
  customBaseDomain: string | null
  termsUrl: string | null
  privacyPolicyUrl: string | null
  supportEmail: string | null
  supportUrl: string | null
  supportPhone: string | null
  zendeskFormUrl: string | null
  zendeskFormId: string | null
  // OEM: partner 名義の Chrome ウェブストア拡張機能 URL。LOCKED は LOCKED の URL、partner は設定値、
  // partner 未設定 / 完全 degraded は null（null のとき拡張 DL ボタンを出さない）。
  chromeWebStoreUrl: string | null
  // OEM: partner のブランドプライマリカラー（#RRGGBB）。BrandProvider が CSS 変数 --brand-primary に
  // 流し、tailwind の `locked` 色（var(--brand-primary, 既定)）と直書き hex の var 化箇所が追従する。
  // 未設定 / LOCKED は null で既定色のまま。
  brandColor: string | null
  // OEM: partner が設定した認証画面のブランドロゴ表示幅（px）。ロゴの縦横比は partner ごとに
  // 異なり既定の 96px 固定では収まりが悪いため、Console から調整できる。
  // 未設定 / LOCKED は null で既定幅（resolveLogoWidth の 96px）にフォールバックする。
  logoWidth: number | null
  fallback: boolean
}

// fetch のタイムアウト（ms）。無期限スピナーを防ぎ、タイムアウトは他のネットワーク失敗と
// 同様に null として扱う（→ BrandContext のリトライ経路に乗る）。
const BRAND_FETCH_TIMEOUT_MS = 5000

// AbortSignal.timeout が無い環境（古い jsdom / 型定義に含まれない lib）向けの fallback。
// AbortSignal.timeout は型定義（tsconfig lib）に無い場合があるため動的に参照する。
const timeoutSignal = (ms: number): AbortSignal => {
  const signalCtor = AbortSignal as unknown as {
    timeout?: (ms: number) => AbortSignal
  }
  if (typeof signalCtor.timeout === 'function') {
    return signalCtor.timeout(ms)
  }
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

// same-origin の locked-auth public brand endpoint（案E）を叩く。
// host は渡さず、locked-auth が request.host を権威として tenant/brand を解決する
// （client 入力を信頼しない / host 権威の一貫性。本番は SPA と auth API が同一オリジン）。
// 失敗（非 2xx / オフライン / DNS / CORS / timeout）はすべて null に正規化して返す。
// これにより throw 系の失敗も BrandContext のリトライ経路（数回→fallback）に乗る。
export const getBrand = async (): Promise<Brand | null> => {
  const base = fullUrl('auth')
  if (!base) return null
  try {
    const res = await fetch(`${base}/public/brand`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: timeoutSignal(BRAND_FETCH_TIMEOUT_MS),
    })
    if (!res.ok) return null
    return (await res.json()) as Brand
  } catch {
    // network error / timeout / CORS / JSON parse 失敗を非 2xx と同じ null に正規化する。
    return null
  }
}
